import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { AppService } from "@irb/app.service";
import { UtilityService } from "@irb-common/services/utility.service";
import { IGlobalSettings } from "@irb/irb-settings/irb-settings.model";
import { ISystemMessage } from '@irb/irb-dashboard/irb-dashboard.model';
import { shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: "root",
})
export class IrbSettingsService {
  httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      Expires: "Sat, 01 Jan 2000 00:00:00 GMT",
    }),
  };

  _expandedPanels: Array<string> = [];
  _defaultExpandedPanels: Array<string> = [];
  maxPanels: number;
  panelName: string;
  getGlobalSettingsObservable: Observable<Object>;
  private dialogData: any;
  importSecJobTimerOn$ = new BehaviorSubject<boolean>(true);
  public subscriptionTime: number;

  constructor(private _http: HttpClient, private as: AppService, private util: UtilityService) {

  }

  updateExpansionState(itemName: string, newState: string) {
    const index = this.expandedPanels.indexOf(itemName);

    if (index === -1 && newState === "opened") {
      this.expandedPanels.push(itemName);
    } else if (newState === "closed") {
      this.expandedPanels.splice(index, 1);
    }

    this.saveExpansionState();
  }

  getDefaultExpansionState(itemName: string) {
    return this.defaultExpandedPanels.indexOf(itemName) !== -1;
  }

  loadExpansionState(initMaxPanels: number, initPanelName: string, defaultExpandedPanels?: Array<string>) {
    this.maxPanels = initMaxPanels;
    this.panelName = initPanelName;
    const dep = defaultExpandedPanels ? defaultExpandedPanels : [];
    this.defaultExpandedPanels = this.as.getFromSession(this.panelName) || dep;
    this.expandedPanels = this.util.clone(this.defaultExpandedPanels);
  }

  saveExpansionState() {
    this.as.setToSession(this.panelName, this.expandedPanels);
  }

  getGlobalSettings(): Observable<Object> {
    const currentTime = new Date().getTime();
    if (!this.subscriptionTime || currentTime - this.subscriptionTime > 2000) {
      this.getGlobalSettingsObservable = null;
    }
    if (!this.getGlobalSettingsObservable) {
      this.getGlobalSettingsObservable = this._http.get("/irebalone/settings/get-global-settings", this.httpOptions).pipe(shareReplay());
    }
    this.subscriptionTime = currentTime;
    return this.getGlobalSettingsObservable;
  }

  saveGlobalSettings(globalSettings: IGlobalSettings): Observable<Object> {
    return this._http.post("/irebalone/settings/save-global-settings", JSON.stringify(globalSettings), this.httpOptions);
  }

  getViewSuggestionValues(): Observable<Object> {
    return this._http.get("/irebalone/settings/get-view-suggestion-values", this.httpOptions);
  }
  getGainsControlsDropDown(): Observable<Object> {
    return this._http.get("/irebalone/settings/get-gains-controls-dropdown", this.httpOptions);
  }
  saveSecurityAndPortfolioGlobalSettings(globalSettings: IGlobalSettings): Observable<Object> {
    return this._http.post("/irebalone/settings/save-security-and-portfolio-global-settings", JSON.stringify(globalSettings), this.httpOptions);
  }
  savePortfoliosGainCtrlSettingsAsync(globalSettings: IGlobalSettings): Observable<Object> {
    return this._http.post("/irebalone/settings/save-pf-gain-ctrl-setting-async", JSON.stringify(globalSettings), this.httpOptions);
  }
  savePortfoliosGainCtrlSettingsStatus(statusId: string): Observable<Object> {
    return this._http.get("/irebalone/settings/save-pf-gain-ctrl-setting-async-status/" + statusId);
  }
  get expandedPanels(): Array<string> {
    return this._expandedPanels;
  }

  set expandedPanels(a: Array<string>) {
    this._expandedPanels = a;
  }

  get defaultExpandedPanels(): Array<string> {
    return this._defaultExpandedPanels;
  }

  set defaultExpandedPanels(a: Array<string>) {
    this._defaultExpandedPanels = a;
  }

  getDollarRoundingDropDown(): Observable<Object> {
    return this._http.get("/irebalone/settings/get-dollar-rounding-dropdown", this.httpOptions);
  }

  getShareRoundingDropDown(): Observable<Object> {
    return this._http.get("/irebalone/settings/get-share-rounding-dropdown", this.httpOptions);
  }

  getPriceTypeDropDown(): Observable<Object> {
    return this._http.get("/irebalone/settings/get-price-type-dropdown", this.httpOptions);
  }

  getSMANotAllowed(): Observable<Object> {
    return this._http.get("/irebalone/settings/get-if-sma-not-allowed", this.httpOptions);
  }

  getAutoToleranceDropDown(): Observable<Object> {
    return this._http.get("/irebalone/settings/get-auto-tolerance-dropdown", this.httpOptions);
  }

  getPortfolioCustCategorySettings(): Observable<Object> {
    return this._http.get("/irebalone/settings/get-portfolio-cust-category-settings", this.httpOptions);
  }

  getSecurityCustCategorySettings(): Observable<Object> {
    return this._http.get("/irebalone/settings/get-security-cust-category-settings", this.httpOptions);
  }

  deleteCustCat(custCatId: number): Observable<Object> {
    return this._http.get("/irebalone/settings/delete-cust-cat/" + custCatId, this.httpOptions);
  }

  checkCategoryRestrictions(custCatIds: number[]): Observable<Object> {
    return this._http.post("/irebalone/settings/check-category-restrictions", JSON.stringify(custCatIds), this.httpOptions);
  }

  exportSecurities(data: any): Observable<Blob> {
    return this._http.post("/irebalone/settings/export-security-cust-category-settings", data, { responseType: "blob" });
  }

  exportPortfolios(data: any): Observable<Blob> {
    return this._http.post("/irebalone/settings/export-portfolio-cust-category-settings", data, { responseType: "blob" });
  }

  setDialogData(dialogData: any) {
    this.dialogData = dialogData;
  }

  getDialogData() {
    return this.dialogData;
  }

  saveAnnouncementSettings(data: Array<ISystemMessage>): Observable<Object> {
    return this._http.post("/irebalone/settings/announcements", JSON.stringify(data), this.httpOptions);
  }

  importSecurities(): Observable<Object> {
    return this._http.post("/irebalone/settings/import-securities",  this.httpOptions);
  }

  checkImportSecuritiesJobStatus(): Observable<Object> {
    return this._http.post("/irebalone/settings/check-import-securities-job-status",  this.httpOptions);
  }

  acknowledgeImportSecJob(): Observable<Object> {
    return this._http.post("/irebalone/settings/acknowledge-import-sec-job",  this.httpOptions);
  }

  disableImportSecJobButton(): Observable<Object> {
    return this._http.post("/irebalone/settings/disable-import-sec-button",  this.httpOptions);
  }

  saveModelsGlobalSettings(globalSettings: IGlobalSettings): Observable<Object> {
    return this._http.post("/irebalone/settings/save-models-global-settings", JSON.stringify(globalSettings), this.httpOptions);
  }

  saveRebalancingGlobalSettings(globalSettings: IGlobalSettings): Observable<Object> {
    return this._http.post("/irebalone/settings/save-rebalancing-global-settings", JSON.stringify(globalSettings), this.httpOptions);
  }

  saveTradingGlobalSettings(globalSettings: IGlobalSettings): Observable<Object> {
    return this._http.post("/irebalone/settings/save-trading-global-settings", JSON.stringify(globalSettings), this.httpOptions);
  }

  saveAccountsGlobalSettings(globalSettings: IGlobalSettings): Observable<Object> {
    return this._http.post("/irebalone/settings/save-accounts-global-settings", JSON.stringify(globalSettings), this.httpOptions);
  }

  getTradeTimeDropDown(): Observable<Object> {
    return this._http.get("/irebalone/settings/get-auto-trade-submission-schedule", this.httpOptions);
  }

  getAutoTradeMasterAccountDropDown(): Observable<Object> {
    return this._http.get("/irebalone/block-action/get-master-account", this.httpOptions);
  }

  getAutoTradeUserIDDropDown(): Observable<Object> {
    return this._http.get("/irebalone/settings/get-auto-rebal-trade-user-dropdown", this.httpOptions);
  }
  
  saveAutomatedTradeGlobalSettings(globalSettings: IGlobalSettings): Observable<Object> {
    return this._http.post("/irebalone/settings/automated-trading-settings", JSON.stringify(globalSettings), this.httpOptions);
  }
  saveAutomatedTradeGlobalSettingsStatus(statusId: string): Observable<Object> {
    return this._http.get("/irebalone/settings/auto-trade-setting-status/" + statusId);
  }

  enableAutoCashHoldForAllAcct(): Observable<Object> {
    return this._http.get("/irebalone/accounts/enable-auto-cash-hold-for-all-acct", this.httpOptions);
  }
  getSettingsHistory(params: any, tabName: string) {
    let sort = 'sort(-createdDate)';

    if (params.request.sortModel && params.request.sortModel.length > 0) {
      let sortColumn = params.request.sortModel[0].colId;
      const sortOrder = (params.request.sortModel[0].sort === 'asc') ? '+' : '-';
      sort = 'sort(' + sortOrder + sortColumn + ')';
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT',
      'Filter-Item': 'items=' + params.request.startRow + '-' + (params.request.endRow - 1)
    });

    return this._http.get(`/irebalone/settings/global-settings-history/${tabName}?${sort}`, { headers, observe: 'response' } );
  }
  exportSettingsHistory(tabName: string) {
    const payload = {
      tabName: tabName
    };
    return this._http.post("/irebalone/settings/async/export-setting-history", JSON.stringify(payload), this.httpOptions);
  }
}
